import React, {useEffect, useState} from 'react';
import {Form, Header, Message, Segment, Table} from 'semantic-ui-react';
import {useLoaderData, useNavigate} from "react-router-dom";
import {toast} from "react-toastify";
import {useBlockUI} from 'shared/services/block-ui-service';
import {ToasterConfig} from "../../../../config/ToasterConfig";
import useManagerService from "../../../../services/backoffice/hooks/useManagerService";
import useClientService from "../../../../services/backoffice/hooks/useClientService";
import {ShortDto} from "../../../../models/short-dto";
import {UpdateManagerRequest} from "../../../../models/requests/update-manager-request";
import {SessionUser} from "../../../../models/session-user";
import SessionStorage from "../../../../store/session";

export async function editManagerLoader({params}: any) {
    return params.id;
}

const EditManagerPage = () => {
    const [manager, setManager] = useState<Partial<UpdateManagerRequest>>();
    const [loading, setLoading] = useState<boolean>(false);
    const [clientes, setClients] = useState<ShortDto[]>([])
    const [error, setError] = useState<string>('');
    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [sessionUser, setSessionUser] = useState<SessionUser>();
    const navigate = useNavigate();
    const loader: any = useLoaderData();
    const blockUI = useBlockUI();
    const {getManagerById, editManager, changeManagerPassword} = useManagerService();
    const {getAllClientsShortDto} = useClientService();
    const isAdmin = sessionUser?.userData?.profile === 'Admin';
    const isManager = sessionUser?.userData?.profile === 'Manager';

    useEffect(() => {
        async function updateSessionUser() {
            const user = await SessionStorage.getSession();
            setSessionUser(user)
        }

        updateSessionUser()
    }, [])

    useEffect(() => {
        const managerId = loader;
        if (!managerId) return;
        getManagerById(managerId).then((response: any) => {
            setManager(response)
        }).catch((error: any) => {
            const message = error?.response?.data?.errors?.[0]
                ?? error?.response?.data?.message
                ?? 'Falha ao carregar gestor';
            toast.warn(message, ToasterConfig.error);
            navigate('/backoffice/managers/');
        })
    }, [loader])

    useEffect(() => {
        blockUI?.setActive(loading)
    }, [loading]);

    useEffect(() => {
        if (!isAdmin) return;

        function ListClientes() {
            getAllClientsShortDto().then((response) => {
                setClients(response);
            }).catch(() => {});
        }

        ListClientes()
    }, [isAdmin])

    const handleClienteIdChange = (e: any, data: any) => {
        const selectedValue = data.value;
        setManager(prevGestor => ({...prevGestor, clienteId: selectedValue}));
    };

    const updateManager = async () => {
        try {
            setLoading(true);
            if (manager) {
                const result = await editManager(manager, manager.id);
                if (!!result) {
                    navigate('/backoffice/managers/');
                    toast.success('Gestor atualizado com sucesso', ToasterConfig.success);
                }
            }
        } catch (error: any) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const changePassword = async () => {
        if (!manager?.id) return;

        if (password !== passwordConfirmation) {
            toast.warn('A senha e a confirmação devem ser iguais', ToasterConfig.error);
            return;
        }

        try {
            setLoading(true);
            const result = await changeManagerPassword(manager.id, {
                password,
                passwordConfirmation,
            });
            toast.success(result.message, ToasterConfig.success);
            setPassword('');
            setPasswordConfirmation('');
        } catch (error: any) {
            const message = error?.response?.data?.errors?.[0]
                ?? error?.response?.data?.message
                ?? 'Falha ao alterar senha do gestor';
            toast.warn(message, ToasterConfig.error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Header as="h3" textAlign="center" color={'green'}>
                Gestores / Editar
            </Header>
            {isAdmin && (
                <Form size="large" onSubmit={updateManager}>
                    <Segment stacked>
                        <Form.Input
                            fluid
                            label="Email"
                            placeholder="Email"
                            name="email"
                            value={manager?.email}
                            onChange={(event) => setManager({...manager, email: event.target.value})}
                            required
                        />

                        <Form.Input
                            fluid
                            label="Nome"
                            placeholder="Nome"
                            name="firstName"
                            value={manager?.firstName}
                            onChange={(event) => setManager({...manager, firstName: event.target.value})}
                            required
                        />

                        <Form.Input
                            fluid
                            label="Sobrenome"
                            placeholder="Sobrenome"
                            name="lastName"
                            value={manager?.lastName}
                            onChange={(event) => setManager({...manager, lastName: event.target.value})}
                            required
                        />

                        <Form.Select
                            label={'Cliente ao qual o gestor pertence'}
                            options={clientes}
                            placeholder='Selecione uma opção'
                            value={manager?.clienteId || ''}
                            required
                            onChange={handleClienteIdChange}
                        />

                        <Table.Row>
                            <Table.HeaderCell>
                                <Form.Button content="Voltar"
                                             icon='arrow left'
                                             onClick={() => navigate('/backoffice/managers/')} grey/>
                            </Table.HeaderCell>
                            <Table.HeaderCell>
                                <Form.Button content="Atualizar"
                                             type={"submit"}
                                             icon='save'
                                             color={"green"}/>
                            </Table.HeaderCell>
                        </Table.Row>
                        {error && <Message negative>{error}</Message>}
                    </Segment>
                </Form>
            )}

            {!isAdmin && isManager && (
                <Segment stacked>
                    <Form.Button content="Voltar"
                                 icon='arrow left'
                                 onClick={() => navigate('/backoffice/managers/')} grey/>
                    <Header as="h4">{manager?.firstName} {manager?.lastName}</Header>
                    <p>{manager?.email}</p>
                </Segment>
            )}

            {(isAdmin || isManager) && (
                <Segment stacked>
                    <Header as="h4" color="green">Alterar senha do gestor</Header>
                    <Form>
                        <Form.Input
                            fluid
                            label="Nova senha"
                            type="password"
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                        />
                        <Form.Input
                            fluid
                            label="Confirmação da senha"
                            type="password"
                            value={passwordConfirmation}
                            onChange={(event) => setPasswordConfirmation(event.target.value)}
                        />
                        <Form.Button
                            type="button"
                            color="orange"
                            icon="key"
                            content="Alterar senha"
                            onClick={changePassword}
                        />
                    </Form>
                </Segment>
            )}
        </div>
    );
};

export default EditManagerPage;
